import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { Field } from "../components/Field";

const SUBJECTS = ["Proposal or delivery partner", "Partnerships", "Media enquiry", "Player pathway guidance", "Volunteering", "Something else"];

// The nav sends ?subject=media or ?subject=partnerships so the two doors land on different enquiries.
const PRESET = { media: "Media enquiry", partnerships: "Partnerships", proposal: "Proposal or delivery partner" };

export default function Contact() {
  const [params] = useSearchParams();
  const preset = PRESET[params.get("subject")] || SUBJECTS[0];
  const [form, setForm] = useState({ name: "", email: "", organisation: "", subject: preset, message: "" });
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    setForm((f) => ({ ...f, subject: preset }));
  }, [preset]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useSeo({
    title: "Media Enquiries, Advertising and Partnerships — KickStartKenyaFootball",
    description: "Media enquiries, partnerships and player representation. Talk to the KickStartKenyaFootball team.",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.email_sent ? "Enquiry sent. Check your inbox for confirmation." : "Enquiry received. We will be in touch.");
      setForm({ name: "", email: "", organisation: "", subject: preset, message: "" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="contact-page">
      <div className="mx-auto grid max-w-[1500px] gap-16 px-5 md:px-10 lg:grid-cols-[1fr_1.1fr]">
        <Reveal>
          <p className="overline">Get in touch</p>
          <p className="eyebrow">Media enquiries</p>
          <h1 className="display-1 mt-5">Media Enquiries</h1>
          <p className="body-copy mt-7 max-w-md">
            Sponsors, county governments, federations, clubs, schools, contractors, coach educators, journalists and
            families of players in our Hall of Fame — this reaches our team directly.
          </p>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-500">
            Have a concrete plan for a facility, academy, futsal court, stadium or step? Use the{" "}
            <Link to="/apply" className="text-ochre underline decoration-maasai underline-offset-4" data-testid="contact-proposals-link">
              proposals page
            </Link>{" "}
            instead — it captures the detail we need. Please note we are not running player trials at present; anything we
            publish on trials and academies is general guidance.
          </p>
          <div className="mt-12 border-t border-zinc-900 pt-8" id="partnerships" data-testid="contact-partnerships">
            <h2 className="display-3">Advertising and Partnerships</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
              Shirt fronts, court naming, county academy sponsorship, broadcast and diaspora campaigns. Tell us the
              audience you want to reach and the counties you care about, and we will come back with what is available
              and what it would cost.
            </p>
          </div>
          <div className="mt-10 space-y-6 border-t border-zinc-900 pt-8">
            <div className="flex items-start gap-4">
              <Mail size={18} className="mt-0.5 text-maasai" />
              <div>
                <p className="overline">Media enquiries</p>
                <p className="mt-1.5 text-sm text-white" data-testid="contact-email-media">
                  media@kickstartkenyafootball.com
                </p>
                <p className="mt-4 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">
                  Advertising and partnerships
                </p>
                <p className="mt-1.5 text-sm text-white" data-testid="contact-email-partnerships">
                  partnerships@kickstartkenyafootball.com
                </p>
                <p className="mt-4 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">Everything else</p>
                <p className="mt-1.5 text-sm text-white" data-testid="contact-email">
                  {settings.contact_email || "info@kickstartkenyafootball.com"}
                </p>
              </div>
            </div>
            <div>
              <p className="overline">Headquarters</p>
              <p className="mt-1.5 text-sm text-white">Nairobi, Kenya · sub branches in Kisumu and Mombasa</p>
            </div>
            <div>
              <p className="overline">Diaspora chapters</p>
              <p className="mt-1.5 text-sm text-white">London · Washington DC · Dubai · Toronto · Brussels</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} className="grid gap-5 border border-zinc-900 bg-ink-surface p-7 sm:grid-cols-2 md:p-10" data-testid="contact-page-form">
            <Field label="Full name" required>
              <input required className="field" placeholder="Jane Achieng" aria-label="Full name" data-testid="contact-page-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Email" required>
              <input required type="email" className="field" placeholder="you@example.com" aria-label="Email" data-testid="contact-page-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Organisation">
              <input className="field" placeholder="Club, county, company or none" aria-label="Organisation" data-testid="contact-page-org" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} />
            </Field>
            <Field label="Subject">
              <select className="field appearance-none" aria-label="Subject" data-testid="contact-page-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Your message" required className="sm:col-span-2">
              <textarea required rows={6} className="field" placeholder="Tell us what you are proposing, asking or offering" aria-label="Message" data-testid="contact-page-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </Field>
            <button type="submit" disabled={busy} className="btn-primary justify-center disabled:opacity-60 sm:col-span-2" data-testid="contact-page-submit">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />} Send enquiry
            </button>
            <p className="text-xs leading-relaxed text-zinc-500 sm:col-span-2" data-testid="contact-privacy">
              We use these details only to answer your enquiry and keep a record of it. No marketing unless you
              ask for it.{" "}
              <Link to="/privacy" className="text-ochre underline">How we use your data</Link>.
            </p>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
